import React, { useState } from 'react';
import { 
  HelpCircle, Book, MessageCircle, Mail, ExternalLink, 
  ChevronDown, ChevronRight, Search, FileText, Video,
  Phone, Clock, CheckCircle
} from 'lucide-react';

const HelpSupportPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: ''
  });

  const faqs = [
    {
      id: 1,
      question: 'How do I upload project data?',
      answer: 'Navigate to the Upload Center from the sidebar. Select your project, then drag and drop your Excel file or click to browse. The system supports .xlsx and .xls formats with our standard template.'
    },
    {
      id: 2,
      question: 'What user roles are available?',
      answer: 'ScopeOps has 5 roles: Super Admin (full access), Tenant Admin (tenant-wide management), PMO (portfolio oversight), Project Manager (project-level access), and Viewer (read-only access). Each role has specific permissions that can be viewed in Admin > Roles & Permissions.'
    },
    {
      id: 3,
      question: 'How do I change my billing plan?',
      answer: 'Go to Admin > Billing to view and manage your subscription. You can upgrade or downgrade your plan at any time. Changes take effect immediately, with prorated billing for upgrades.'
    },
    {
      id: 4,
      question: 'Can I customize the dashboard?',
      answer: 'Yes! Use the filter controls to customize which projects, programs, or portfolios appear on your dashboard. Your preferences are saved automatically. For organization-wide customization, admins can use Brand & Theming settings.'
    },
    {
      id: 5,
      question: 'How do I export reports?',
      answer: 'Each dashboard and data view includes an Export button. You can export to Excel, PDF, or CSV formats. For scheduled exports, contact support to enable this feature for your organization.'
    },
    {
      id: 6,
      question: 'What is version history in uploads?',
      answer: 'Every time you upload a new Excel file for a project, it creates a new version. You can view all versions, see who uploaded them, and activate any previous version if needed. Only one version can be active at a time.'
    }
  ];

  const resources = [
    {
      title: 'Getting Started Guide',
      description: 'Learn the basics of ScopeOps in 10 minutes',
      icon: Book,
      type: 'Documentation'
    },
    {
      title: 'Excel Template Guide',
      description: 'How to structure your data for upload',
      icon: FileText,
      type: 'Documentation'
    },
    {
      title: 'Video Tutorials',
      description: 'Step-by-step video walkthroughs',
      icon: Video,
      type: 'Video'
    },
    {
      title: 'API Documentation',
      description: 'Technical docs for developers',
      icon: Book,
      type: 'Documentation'
    }
  ];

  const handleSubmitTicket = (e) => {
    e.preventDefault();
    // TODO: Implement ticket submission
    alert('Support ticket submitted! We\'ll respond within 24 hours.');
    setTicketForm({ subject: '', category: '', priority: 'medium', description: '' });
  };

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
          <HelpCircle className="w-7 h-7 text-[var(--clr-sev-healthy)]" />
          Help & Support
        </h1>
        <p className="text-[var(--clr-text-secondary)] mt-1">Find answers, documentation, and contact support</p>
      </div>

      {/* Quick Contact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)] hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            </div>
            <h3 className="font-semibold text-[var(--clr-text-primary)]">Email Support</h3>
          </div>
          <p className="text-sm text-[var(--clr-text-secondary)] mb-3">Get help via email within 24 hours</p>
          <a 
            href="mailto:support@scopeops.ai" 
            className="text-[var(--clr-brand-primary)] hover:underline text-sm flex items-center gap-1"
          >
            support@scopeops.ai
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)] hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MessageCircle className="w-5 h-5 text-[var(--clr-sev-healthy)]" />
            </div>
            <h3 className="font-semibold text-[var(--clr-text-primary)]">Live Chat</h3>
          </div>
          <p className="text-sm text-[var(--clr-text-secondary)] mb-3">Chat with us during business hours</p>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-[var(--clr-text-muted)]" />
            <span className="text-[var(--clr-text-secondary)]">Mon-Fri, 8AM-6PM SAST</span>
          </div>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)] hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Phone className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-[var(--clr-text-primary)]">Phone Support</h3>
          </div>
          <p className="text-sm text-[var(--clr-text-secondary)] mb-3">Premium support for Enterprise plans</p>
          <span className="text-sm text-[var(--clr-text-muted)]">Available for Enterprise tier</span>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Frequently Asked Questions</h2>
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--clr-text-muted)]" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              data-testid="faq-search-input"
            />
          </div>
        </div>
        <div className="divide-y">
          {filteredFaqs.map(faq => (
            <div key={faq.id} className="p-4">
              <button
                onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                className="w-full flex items-center justify-between text-left"
                data-testid={`faq-${faq.id}`}
              >
                <span className="font-medium text-[var(--clr-text-primary)]">{faq.question}</span>
                {expandedFaq === faq.id ? (
                  <ChevronDown className="w-5 h-5 text-[var(--clr-text-muted)]" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-[var(--clr-text-muted)]" />
                )}
              </button>
              {expandedFaq === faq.id && (
                <p className="mt-3 text-[var(--clr-text-secondary)] text-sm pl-0">{faq.answer}</p>
              )}
            </div>
          ))}
          {filteredFaqs.length === 0 && (
            <div className="p-8 text-center text-[var(--clr-text-muted)]">
              <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p>No FAQs match your search</p>
            </div>
          )}
        </div>
      </div>

      {/* Resources */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Resources & Documentation</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          {resources.map((resource, idx) => {
            const Icon = resource.icon;
            return (
              <div 
                key={idx}
                className="p-4 border rounded-lg hover:bg-[var(--clr-surface-hover)] cursor-pointer flex items-start gap-4"
                data-testid={`resource-${idx}`}
              >
                <div className="p-2 bg-[var(--clr-surface-subtle)] rounded-lg">
                  <Icon className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[var(--clr-text-primary)]">{resource.title}</h3>
                  <p className="text-sm text-[var(--clr-text-secondary)] mt-1">{resource.description}</p>
                  <span className="text-xs text-[var(--clr-brand-primary)] mt-2 inline-block">{resource.type}</span>
                </div>
                <ExternalLink className="w-4 h-4 text-[var(--clr-text-muted)]" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Submit Ticket */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Submit a Support Ticket</h2>
          <p className="text-sm text-[var(--clr-text-secondary)] mt-1">Can't find what you need? Open a support ticket</p>
        </div>
        <form onSubmit={handleSubmitTicket} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                type="text"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                placeholder="Brief description of your issue"
                required
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={ticketForm.category}
                onChange={(e) => setTicketForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                required
                data-testid="ticket-category-select"
              >
                <option value="">Select a category</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing & Subscription</option>
                <option value="feature">Feature Request</option>
                <option value="account">Account & Access</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <div className="flex gap-4">
              {['low', 'medium', 'high'].map(priority => (
                <label key={priority} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="priority"
                    value={priority}
                    checked={ticketForm.priority === priority}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="text-[var(--clr-brand-primary)]"
                    data-testid={`ticket-priority-${priority}`}
                  />
                  <span className="capitalize text-sm text-gray-700">{priority}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={ticketForm.description}
              onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] h-32"
              placeholder="Provide as much detail as possible..."
              required
              data-testid="ticket-description-input"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-2"
              data-testid="submit-ticket-btn"
            >
              <CheckCircle className="w-4 h-4" />
              Submit Ticket
            </button>
          </div>
        </form>
      </div>

      {/* System Status */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-[var(--clr-sev-healthy)] rounded-full animate-pulse" />
            <span className="font-medium text-[var(--clr-text-primary)]">All Systems Operational</span>
          </div>
          <a href="#" className="text-sm text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1">
            View Status Page
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default HelpSupportPage;
